import { atxHeadingLinePattern, fenceLinePattern } from "./constants";

export type FoldRange = {
  /** First body line index (exclusive of the heading). */
  start: number;
  /** Exclusive end index. */
  end: number;
};

export type EditorHeading = {
  index: number;
  level: number;
  title: string;
  /** Stable id: `${level}:${title}:${occurrence}` */
  key: string;
  foldRange: FoldRange;
  foldable: boolean;
};

export type FoldAnchor = {
  /** How many visible lines precede this hidden block. */
  afterVisibleCount: number;
  lines: string[];
};

/** ATX heading level, or null if the line is not a heading. */
export function getHeadingLevel(line: string): number | null {
  const match = /^(\s{0,3})(#{1,6})\s+/.exec(line);

  if (!match) {
    return null;
  }

  return match[2]!.length;
}

export function getHeadingTitle(line: string): string {
  return line.replace(/^\s{0,3}#{1,6}\s+/, "").trim();
}

/**
 * Body range for a heading: lines after it until the next heading of the same
 * or higher level (or EOF). Fence-aware so `#` inside code does not end a fold.
 */
export function getFoldRange(
  lines: readonly string[],
  headingIndex: number,
  level: number,
): FoldRange {
  const start = headingIndex + 1;
  let inFence = false;

  for (let index = 0; index <= headingIndex; index += 1) {
    if (fenceLinePattern.test(lines[index] ?? "")) {
      inFence = !inFence;
    }
  }

  for (let index = start; index < lines.length; index += 1) {
    const line = lines[index] ?? "";

    if (fenceLinePattern.test(line)) {
      inFence = !inFence;
      continue;
    }

    if (inFence) {
      continue;
    }

    const nextLevel = getHeadingLevel(line);

    if (nextLevel !== null && nextLevel <= level) {
      return { start, end: index };
    }
  }

  return { start, end: lines.length };
}

/** All ATX headings outside fences, with fold metadata and stable keys. */
export function scanEditorHeadings(lines: readonly string[]): EditorHeading[] {
  const drafts: Array<{ index: number; level: number; title: string }> = [];
  let inFence = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";

    if (fenceLinePattern.test(line)) {
      inFence = !inFence;
      continue;
    }

    if (inFence) {
      continue;
    }

    const level = getHeadingLevel(line);

    if (level === null) {
      continue;
    }

    drafts.push({
      index,
      level,
      title: getHeadingTitle(line),
    });
  }

  const occurrenceByBase = new Map<string, number>();

  return drafts.map((draft) => {
    const base = `${draft.level}:${draft.title}`;
    const occurrence = occurrenceByBase.get(base) ?? 0;
    occurrenceByBase.set(base, occurrence + 1);
    const foldRange = getFoldRange(lines, draft.index, draft.level);

    return {
      ...draft,
      key: `${base}:${occurrence}`,
      foldRange,
      foldable: foldRange.end > foldRange.start,
    };
  });
}

export function pruneCollapsedHeadingKeys(
  headings: readonly EditorHeading[],
  collapsedKeys: ReadonlySet<string>,
): Set<string> {
  const foldableKeys = new Set(
    headings.filter((heading) => heading.foldable).map((heading) => heading.key),
  );
  const next = new Set<string>();

  for (const key of collapsedKeys) {
    if (foldableKeys.has(key)) {
      next.add(key);
    }
  }

  return next;
}

/** Line indexes hidden by any collapsed foldable heading. */
export function getHiddenLineIndexes(
  headings: readonly EditorHeading[],
  collapsedKeys: ReadonlySet<string>,
): Set<number> {
  const hidden = new Set<number>();

  for (const heading of headings) {
    if (!heading.foldable || !collapsedKeys.has(heading.key)) {
      continue;
    }

    for (
      let index = heading.foldRange.start;
      index < heading.foldRange.end;
      index += 1
    ) {
      hidden.add(index);
    }
  }

  return hidden;
}

export function getVisibleFullLineIndexes(
  lineCount: number,
  hidden: ReadonlySet<number>,
): number[] {
  const indexes: number[] = [];

  for (let index = 0; index < lineCount; index += 1) {
    if (!hidden.has(index)) {
      indexes.push(index);
    }
  }

  return indexes;
}

export function buildVisibleMarkdown(
  lines: readonly string[],
  hidden: ReadonlySet<number>,
): string {
  return lines.filter((_, index) => !hidden.has(index)).join("\n");
}

export function extractFoldAnchors(
  fullLines: readonly string[],
  hidden: ReadonlySet<number>,
): { visibleLines: string[]; anchors: FoldAnchor[] } {
  const visibleLines: string[] = [];
  const anchors: FoldAnchor[] = [];
  let index = 0;

  while (index < fullLines.length) {
    if (hidden.has(index)) {
      const block: string[] = [];
      const afterVisibleCount = visibleLines.length;

      while (index < fullLines.length && hidden.has(index)) {
        block.push(fullLines[index] ?? "");
        index += 1;
      }

      anchors.push({ afterVisibleCount, lines: block });
      continue;
    }

    visibleLines.push(fullLines[index] ?? "");
    index += 1;
  }

  return { visibleLines, anchors };
}

/**
 * Map each previous visible line index to its index in `next`, or null if that
 * line was deleted. Exact LCS matches first; remaining 1:1 gaps are treated as
 * in-place edits so renaming a folded heading keeps its body.
 */
export function mapVisibleLineIndexes(
  previous: readonly string[],
  next: readonly string[],
): Array<number | null> {
  const prevCount = previous.length;
  const nextCount = next.length;
  const table: number[][] = Array.from({ length: prevCount + 1 }, () =>
    Array.from({ length: nextCount + 1 }, () => 0),
  );

  for (let i = prevCount - 1; i >= 0; i -= 1) {
    for (let j = nextCount - 1; j >= 0; j -= 1) {
      if (previous[i] === next[j]) {
        table[i]![j] = (table[i + 1]![j + 1] ?? 0) + 1;
      } else {
        table[i]![j] = Math.max(table[i + 1]![j] ?? 0, table[i]![j + 1] ?? 0);
      }
    }
  }

  const mapping: Array<number | null> = Array.from(
    { length: prevCount },
    () => null,
  );
  const matchedNext = new Set<number>();
  let i = 0;
  let j = 0;

  while (i < prevCount && j < nextCount) {
    if (previous[i] === next[j]) {
      mapping[i] = j;
      matchedNext.add(j);
      i += 1;
      j += 1;
      continue;
    }

    if ((table[i + 1]![j] ?? 0) >= (table[i]![j + 1] ?? 0)) {
      i += 1;
    } else {
      j += 1;
    }
  }

  // Pair unmatched spans between LCS matches as substitutions (same length).
  let prevCursor = 0;
  let nextCursor = 0;

  while (prevCursor < prevCount || nextCursor < nextCount) {
    while (prevCursor < prevCount && mapping[prevCursor] !== null) {
      nextCursor = (mapping[prevCursor] as number) + 1;
      prevCursor += 1;
    }
    while (nextCursor < nextCount && matchedNext.has(nextCursor)) {
      nextCursor += 1;
    }

    const unmatchedPrev: number[] = [];
    while (prevCursor < prevCount && mapping[prevCursor] === null) {
      unmatchedPrev.push(prevCursor);
      prevCursor += 1;
    }

    const nextLimit =
      prevCursor < prevCount && mapping[prevCursor] !== null
        ? (mapping[prevCursor] as number)
        : nextCount;
    const unmatchedNext: number[] = [];
    while (nextCursor < nextLimit) {
      if (!matchedNext.has(nextCursor)) {
        unmatchedNext.push(nextCursor);
      }
      nextCursor += 1;
    }

    const shared = Math.min(unmatchedPrev.length, unmatchedNext.length);
    for (let index = 0; index < shared; index += 1) {
      mapping[unmatchedPrev[index]!] = unmatchedNext[index]!;
    }
  }

  return mapping;
}

/**
 * Splice a visible-editor edit back into the full document, preserving hidden
 * fold bodies after collapsed headings that remain ATX headings. Removing the
 * heading line, or clearing it via selection so it is no longer a heading,
 * drops the hidden body with it.
 */
export function mergeVisibleMarkdown(
  fullLines: readonly string[],
  hiddenIndexes: ReadonlySet<number>,
  nextVisibleValue: string,
): string {
  if (hiddenIndexes.size === 0) {
    return nextVisibleValue;
  }

  const { visibleLines: previousVisible, anchors } = extractFoldAnchors(
    fullLines,
    hiddenIndexes,
  );
  const nextVisibleLines = nextVisibleValue.split("\n");
  const mapping = mapVisibleLineIndexes(previousVisible, nextVisibleLines);

  const leadingBodies: string[] = [];
  const bodiesAfterNextIndex = new Map<number, string[]>();

  for (const anchor of anchors) {
    if (anchor.afterVisibleCount === 0) {
      leadingBodies.push(...anchor.lines);
      continue;
    }

    const ownerIndex = anchor.afterVisibleCount - 1;
    const nextOwnerIndex = mapping[ownerIndex] ?? null;

    // Heading line removed entirely — drop the hidden body.
    if (nextOwnerIndex === null) {
      continue;
    }

    // Cleared via selection (empty / non-heading) while staying on the line.
    if (getHeadingLevel(nextVisibleLines[nextOwnerIndex] ?? "") === null) {
      continue;
    }

    const existing = bodiesAfterNextIndex.get(nextOwnerIndex);
    if (existing) {
      existing.push(...anchor.lines);
    } else {
      bodiesAfterNextIndex.set(nextOwnerIndex, [...anchor.lines]);
    }
  }

  const result: string[] = [...leadingBodies];

  for (let index = 0; index < nextVisibleLines.length; index += 1) {
    result.push(nextVisibleLines[index] ?? "");
    const body = bodiesAfterNextIndex.get(index);
    if (body) {
      result.push(...body);
    }
  }

  return result.join("\n");
}

/**
 * When the visible selection fully covers one or more collapsed fold headings,
 * return clipboard text with each heading's hidden body included after it.
 * Returns null when no folded heading is fully selected (use native clipboard).
 */
export function getCollapsedFoldClipboardText(
  fullLines: readonly string[],
  hiddenIndexes: ReadonlySet<number>,
  visibleValue: string,
  selection: { start: number; end: number },
): string | null {
  const start = Math.min(selection.start, selection.end);
  const end = Math.max(selection.start, selection.end);

  if (start === end || hiddenIndexes.size === 0) {
    return null;
  }

  const { visibleLines, anchors } = extractFoldAnchors(fullLines, hiddenIndexes);
  const bodyByOwner = new Map<number, readonly string[]>();

  for (const anchor of anchors) {
    if (anchor.afterVisibleCount > 0) {
      bodyByOwner.set(anchor.afterVisibleCount - 1, anchor.lines);
    }
  }

  if (bodyByOwner.size === 0) {
    return null;
  }

  const lineStarts: number[] = [];
  let offset = 0;
  for (let index = 0; index < visibleLines.length; index += 1) {
    lineStarts.push(offset);
    offset +=
      (visibleLines[index] ?? "").length +
      (index < visibleLines.length - 1 ? 1 : 0);
  }

  const ownersToExpand: number[] = [];
  for (const ownerIndex of bodyByOwner.keys()) {
    const lineStart = lineStarts[ownerIndex];
    if (lineStart === undefined) {
      continue;
    }
    const contentEnd = lineStart + (visibleLines[ownerIndex] ?? "").length;
    if (start <= lineStart && end >= contentEnd) {
      ownersToExpand.push(ownerIndex);
    }
  }

  if (ownersToExpand.length === 0) {
    return null;
  }

  let clipboard = visibleValue.slice(start, end);
  // Insert from the end so earlier splice offsets stay valid.
  for (const ownerIndex of [...ownersToExpand].sort((a, b) => b - a)) {
    const lineStart = lineStarts[ownerIndex]!;
    const contentEnd = lineStart + (visibleLines[ownerIndex] ?? "").length;
    const headingEndInClipboard = contentEnd - start;
    const bodyText = (bodyByOwner.get(ownerIndex) ?? []).join("\n");
    clipboard =
      clipboard.slice(0, headingEndInClipboard) +
      "\n" +
      bodyText +
      clipboard.slice(headingEndInClipboard);
  }

  return clipboard;
}

/** Offset of the start of a 0-based line within a joined markdown string. */
export function getLineStartOffset(
  lines: readonly string[],
  lineIndex: number,
): number {
  let offset = 0;

  for (let index = 0; index < lineIndex && index < lines.length; index += 1) {
    offset += (lines[index] ?? "").length + 1;
  }

  return offset;
}

export function isAtxHeadingLine(line: string): boolean {
  return atxHeadingLinePattern.test(line);
}
